import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar,
  CheckCircle2, 
  Circle, 
  Clock, 
  Flag, 
  Loader2,
  AlertTriangle,
  ChevronRight,
  User
} from 'lucide-react';
import { format, isAfter, isBefore, startOfDay, addDays } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Task, TaskStatus, TaskPriority } from '@/app/types/task';
import { getMyTasks, markTaskComplete, listTasks } from '@/app/services/taskService';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/app/store/authStore';

interface UpcomingTasksWidgetProps {
  userId?: number;
  limit?: number;
  showOverdue?: boolean;
  showMyTasksOnly?: boolean;
  className?: string;
}

const priorityConfig = {
  [TaskPriority.HIGH]: { label: 'High', color: 'text-red-600', bgColor: 'bg-red-50' },
  [TaskPriority.MEDIUM]: { label: 'Medium', color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
  [TaskPriority.LOW]: { label: 'Low', color: 'text-blue-600', bgColor: 'bg-blue-50' },
};

export default function UpcomingTasksWidget({
  userId,
  limit = 10,
  showOverdue = true,
  showMyTasksOnly = true,
  className,
}: UpcomingTasksWidgetProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  const { data: tasksData, isLoading, error } = useQuery({
    queryKey: showMyTasksOnly 
      ? ['tasks', 'my-tasks', 'upcoming'] 
      : ['tasks', 'all', 'upcoming'],
    queryFn: async () => {
      if (showMyTasksOnly) {
        return await getMyTasks('open', limit);
      } else {
        const endDate = addDays(new Date(), 7);
        return await listTasks({
          status: TaskStatus.OPEN,
          due_date_to: endDate.toISOString(),
          sort_by: 'due_date',
          sort_dir: 'asc',
          limit,
        });
      }
    },
    refetchInterval: 60000,
  });

  const completeMutation = useMutation({
    mutationFn: (taskId: number) => markTaskComplete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Task marked as completed');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to complete task');
    },
  });

  const handleTaskClick = (task: Task) => {
    if (task.parent_entity_type === 'party') {
      navigate(`/party/detail/${task.parent_entity_id}`);
    } else if (task.parent_entity_type === 'voucher') {
      navigate(`/vouchers/${task.parent_entity_id}`);
    }
    // Navigate to tasks page for other entity types or to view/edit
    else {
      navigate('/tasks');
    }
  };

  const handleCompleteTask = (task: Task, e: React.MouseEvent) => {
    e.stopPropagation();
    completeMutation.mutate(task.id);
  };

  const isOverdue = (task: Task) => {
    if (!task.due_date) return false;
    return isBefore(new Date(task.due_date), startOfDay(new Date()));
  };

  const isDueToday = (task: Task) => {
    if (!task.due_date) return false;
    const dueDate = new Date(task.due_date);
    const today = startOfDay(new Date());
    const tomorrow = addDays(today, 1);
    return isAfter(dueDate, today) && isBefore(dueDate, tomorrow);
  };

  const categorizesTasks = (tasks: Task[]) => {
    const overdue: Task[] = [];
    const dueToday: Task[] = [];
    const upcoming: Task[] = [];
    const noDueDate: Task[] = [];

    tasks.forEach(task => {
      if (!task.due_date) {
        noDueDate.push(task);
      } else if (isOverdue(task)) {
        overdue.push(task);
      } else if (isDueToday(task)) {
        dueToday.push(task);
      } else {
        upcoming.push(task);
      }
    });

    return { overdue, dueToday, upcoming, noDueDate };
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">
            {showMyTasksOnly ? 'My Tasks' : 'Upcoming Tasks'}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">
            {showMyTasksOnly ? 'My Tasks' : 'Upcoming Tasks'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            Failed to load tasks
          </div>
        </CardContent>
      </Card>
    );
  }

  const tasks = tasksData?.items || [];
  const { overdue, dueToday, upcoming, noDueDate } = categorizesTasks(tasks);

  const renderTaskItem = (task: Task) => {
    const overdueDays = task.due_date && isOverdue(task)
      ? Math.floor((Date.now() - new Date(task.due_date).getTime()) / (1000 * 60 * 60 * 24))
      : 0;

    return (
      <div
        key={task.id}
        className="flex items-start gap-2 p-2.5 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors group"
        onClick={() => handleTaskClick(task)}
      >
        <Button
          variant="ghost"
          size="sm"
          className="h-4 w-4 p-0 mt-0.5 flex-shrink-0"
          onClick={(e) => handleCompleteTask(task, e)}
        >
          <Circle className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600" />
        </Button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-1">
            <span className="text-xs sm:text-sm font-medium line-clamp-2">{task.title}</span>
            <Badge 
              variant="outline" 
              className={cn(
                "text-xs flex-shrink-0 px-1 py-0 h-5",
                priorityConfig[task.priority].bgColor,
                priorityConfig[task.priority].color
              )}
            >
              <Flag className="h-2.5 w-2.5" />
            </Badge>
          </div>
          
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
            {task.task_category && (
              <Badge variant="outline" className="text-xs px-1.5 py-0 h-4">
                {task.task_category}
              </Badge>
            )}
            {task.due_date && (
              <div className={cn(
                "flex items-center gap-0.5",
                isOverdue(task) && "text-red-600 font-medium",
                isDueToday(task) && "text-yellow-600 font-medium"
              )}>
                <Calendar className="h-2.5 w-2.5" />
                <span className="text-xs">
                  {isOverdue(task) 
                    ? `${overdueDays}d overdue`
                    : isDueToday(task)
                      ? 'Today'
                      : format(new Date(task.due_date), 'MMM dd')
                  }
                </span>
              </div>
            )}
            {task.assigned_to && !showMyTasksOnly && (
              <div className="flex items-center gap-0.5">
                <User className="h-2.5 w-2.5" />
                <span className="truncate max-w-[80px] text-xs">
                  {task.assigned_to.full_name || task.assigned_to.username}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (title: string, tasks: Task[], icon: React.ReactNode, className?: string) => {
    if (tasks.length === 0) return null;
    
    return (
      <div className="space-y-2">
        <div className={cn("flex items-center gap-2 text-sm font-medium", className)}>
          {icon}
          <span>{title}</span>
          <Badge variant="secondary" className="text-xs">
            {tasks.length}
          </Badge>
        </div>
        <div className="grid gap-2 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {tasks.slice(0, limit).map(renderTaskItem)}
        </div>
      </div>
    );
  };

  return (
    <>
      <Card className={className}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              {showMyTasksOnly ? 'My Tasks' : 'Upcoming Tasks'}
              {tasks.length > 0 && (
                <Badge variant="outline" className="text-xs">
                  {tasks.length} open
                </Badge>
              )}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/tasks')}
              title="View all tasks"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground">
                {showMyTasksOnly 
                  ? "No tasks assigned to you" 
                  : "No upcoming tasks"}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {showOverdue && renderSection(
                'Overdue',
                overdue,
                <AlertTriangle className="h-4 w-4" />,
                'text-red-600'
              )}
              
              {renderSection(
                'Due Today',
                dueToday,
                <Clock className="h-4 w-4" />,
                'text-yellow-600'
              )}
              
              {renderSection(
                'Upcoming',
                upcoming,
                <Calendar className="h-4 w-4" />,
                'text-blue-600'
              )}
              
              {renderSection(
                'No Due Date',
                noDueDate,
                <Circle className="h-4 w-4" />,
                'text-gray-600'
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}